import { Messages } from '../../../codegen/Localize';
import { AddLocalUserFormState } from '../../../ts/controllers/AddLocalUserDialogCtrl';
import { L10n } from '../../../ts/L10n/L10n';
import { Users } from '../../../ts/resources/Users';
import { ICheckUserNamesResult, IUserNameAvailability } from '../../../ts/ServerApi/requests/CheckUserNamesRequest';
import { SiteUserAuthSetting } from '../../../ts/ServerApi/types/ISiteUser';
import { AddLocalUserDialogType } from '../../../ts/services/AddLocalUserAction';
import { AuthenticationProviderType } from '../../../ts/services/SiteAuthenticationSettingsService';
import { ServiceLocator } from '../../ServiceLocator';
import { IFormModel } from '../AddLocalUsersDialog';

export interface ISetupLocalUserFormModelChanges {
  formModel: IFormModel;
  sitePickerMenuDisabled: boolean;
}

export class SetupLocalUserModelHelper {

  private Users: Users = ServiceLocator.get<Users>('Users');
  private translate: ng.translate.ITranslateService = ServiceLocator.get<ng.translate.ITranslateService>('$translate');
  private $q: ng.IQService = ServiceLocator.get<ng.IQService>('$q');

  public setupLocalUserFormModel(formModel: IFormModel, userInput: string, addLocalUserDialogType: string, sitePickerMenuDisabled: boolean): ng.IPromise<ISetupLocalUserFormModelChanges>{
    let newFormModel: IFormModel = formModel;
    if (!userInput) {
      newFormModel.formState = AddLocalUserFormState.NewUser;
      newFormModel.newUserFlowModel.usernameInfo = '';
      return this.$q.when({formModel: newFormModel, sitePickerMenuDisabled: sitePickerMenuDisabled} as ISetupLocalUserFormModelChanges);
    }

    return this.Users.checkUserNames([userInput]).then((results: ICheckUserNamesResult) => {
      if (results.users.length === 0) {
        newFormModel.formState = AddLocalUserFormState.Initial;
        return {formModel: newFormModel, sitePickerMenuDisabled: sitePickerMenuDisabled} as ISetupLocalUserFormModelChanges;
      }

      const result: IUserNameAvailability = results.users[0];
      let isSitePickerMenuDisabled = sitePickerMenuDisabled;

      // $note-ltan:
      // We can not assume we are dealing with site users even when existsOnSite is true.
      // Thus the explicity mode check.
      if (addLocalUserDialogType === AddLocalUserDialogType.site && result.existsOnSite) {
        newFormModel = this.setupFormForNotAvailable(result.displayName, 'usernameExistsOnCurrentSite', newFormModel);
      } else if (result.existsOnServer) {
        if (addLocalUserDialogType === AddLocalUserDialogType.site) {
          if ((result.userAuthSetting === SiteUserAuthSetting.Default) && (formModel.authenticationProviderSelectorModel.authenticationProvider.type === AuthenticationProviderType.default)) {
            newFormModel = this.setupFormForAddUserToSite(result.displayName, newFormModel);
            isSitePickerMenuDisabled = true;
          } else {
            newFormModel = this.setupFormForNotAvailable(result.displayName, 'userNameExistsOnServerForAddUserToSiteSAML', newFormModel);
          }
        } else {
          if (result.userAuthSetting === SiteUserAuthSetting.Default) {
            newFormModel = this.setupFormForNotAvailable(result.displayName, 'usernameExistsOnServer', newFormModel);
          } else {
            newFormModel = this.setupFormForNotAvailable(result.displayName, 'userNameExistsOnServerForAddUserToSiteSAML', newFormModel);
          }
        }
      } else {
        newFormModel = this.setupFormForAvailable(newFormModel);
        isSitePickerMenuDisabled = false;
      }
      newFormModel.username = userInput;
      return {formModel: newFormModel, sitePickerMenuDisabled: isSitePickerMenuDisabled} as ISetupLocalUserFormModelChanges;
    });
  }

  public setupFormForNotAvailable(displayName: string, errorStringId: string, formModel: IFormModel): IFormModel {
    let newFormModel: IFormModel = formModel;
    newFormModel.formState = AddLocalUserFormState.NotAvailable;
    newFormModel.notAvailableFlowModel.displayName = displayName;
    newFormModel.notAvailableFlowModel.errorText = this.translate.instant(errorStringId, { USER_NAME: L10n.elideIfLong(displayName) });
    return newFormModel;
  }

  public setupFormForAddUserToSite(displayName: string, formModel: IFormModel): IFormModel {
    let newFormModel: IFormModel = formModel;
    newFormModel.formState = AddLocalUserFormState.OtherSite;
    newFormModel.otherSiteFlowModel.displayName = displayName;
    newFormModel.otherSiteFlowModel.warningText = Messages.usernameExistsOnAnotherSite({USER_NAME: L10n.elideIfLong(displayName)});
    newFormModel.otherSiteFlowModel.proceedClicked = false;
    return newFormModel;
  }

  public setupFormForAvailable(formModel: IFormModel): IFormModel {
    let newFormModel: IFormModel = formModel;
    newFormModel.formState = AddLocalUserFormState.NewUser;
    newFormModel.newUserFlowModel.usernameInfo = Messages.usernameAvailable();
    return newFormModel;
  }
}
